/**
 * ╔═══════════════════════════════════════════════════════════════════╗
 * ║  SINGLE OWNER: User Role Resolution                              ║
 * ║                                                                   ║
 * ║  This hook is the ONLY authority for role checks in the app.      ║
 * ║  - Reads from user_roles table (server-side, not localStorage)    ║
 * ║  - Returns isAdmin / isDriver / isSuperAdmin                     ║
 * ║  - loading=true until resolved — guards MUST wait for this        ║
 * ║  - Subscribes to realtime changes for instant role updates        ║
 * ║                                                                   ║
 * ║  NEVER check roles from localStorage or hardcoded values.         ║
 * ║  NEVER default role to 'admin' — initial state is null.           ║
 * ║  ALWAYS check `loading` before using role in guards.              ║
 * ╚═══════════════════════════════════════════════════════════════════╝
 */
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSupportSession } from "@/lib/supportSession";

export type UserRole = "admin" | "driver" | null;

const SUPER_ADMIN_EMAIL = "aaron@transportmate.co.uk";

export const useUserRole = () => {
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const fetchRole = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        setRole(null);
        setIsSuperAdmin(false);
        setUserId(null);
        setLoading(false);
        return;
      }

      setUserId(user.id);
      setIsSuperAdmin(user.email === SUPER_ADMIN_EMAIL);

      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user role:", error);
      }

      const newRole = (data?.role as UserRole) || null;
      setRole(newRole);
    } catch (e) {
      console.error("[useUserRole] Failed to fetch role:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRole();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      // CRITICAL: Do NOT await inside this callback — it deadlocks the auth state machine.
      // Only re-fetch on SIGNED_IN/SIGNED_OUT. TOKEN_REFRESHED removed because role
      // doesn't change on token refresh and it floods the API on iOS cold starts.
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        fetchRole();
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchRole]);

  // Subscribe to realtime changes on user_roles for the current user
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`user-role-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[useUserRole] Role changed via realtime:', payload.new);
          const newRole = (payload.new as any)?.role as UserRole;
          setRole(newRole || null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Support Mode: a Super Admin impersonating a company must see exactly what
  // that company's owner sees, so the effective role is admin for the session.
  const supportActive = !!getSupportSession();
  const effectiveRole: UserRole = supportActive && isSuperAdmin ? "admin" : role;
  // While supporting a company the admin must be treated as that company's
  // administrator only — every platform-wide affordance stays hidden.
  const effectiveIsSuperAdmin = supportActive ? false : isSuperAdmin;

  return {
    role: effectiveRole,
    loading,
    isAdmin: effectiveRole === "admin",
    isDriver: effectiveRole === "driver",
    isSuperAdmin: effectiveIsSuperAdmin,
    /** True super-admin identity, ignoring any active Support Session */
    isPlatformSuperAdmin: isSuperAdmin,
  };
};
